import React from 'react';

import { formatMoney, parseMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { useSessionStorage } from '@betterment/js-runtime';
import {
  Button,
  FormikField,
  GuidanceBox,
  H1,
  H3,
  HStack,
  RadioGroup,
  Separator,
  Text,
  VStack,
} from '@betterment/samba-react';
import { WorkflowLayout } from '@betterment/shared-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { CoverageSelectionStepOptionsDocument } from './graphql/query.coverageSelectionStepOptions.generated';

import { EmergencyFundCoverageVariant } from '../../../../graphql/schema';
import Footer from '../../../../Shared/components-v2/footer/Footer';
import { convertDisplayableErrors } from '../../../../Shared/displayable-errors';
import { ProductCategory } from '../../../data';
import { useCompleteDraftPurposeFlow } from '../../hooks/useCompleteDraftPurposeFlow';
import { coverage, useEmergencyFundStep } from '../FlowConfig';

type FormValues = Yup.InferType<typeof coverage>;

export const CoverageSelectionStep: React.FC = () => {
  const navigate = useNavigate();
  const { dependencies: monthlyExpensesDependencies } = useEmergencyFundStep('monthlyExpenses');
  const { commit, dependencies } = useEmergencyFundStep('coverage');
  const { flowId } = useParams<{ flowId: string }>();

  // This is a temporary solution until we have a more permanent way to persist the product category
  // through different flows. We want to persist the product category so we can pass it to the AddGoalFlow.
  const { values: productCategory } = useSessionStorage<ProductCategory>('purpose-flow-product-category');

  const monthlyExpensesAmount = parseMoney(dependencies.monthlyExpenses.monthlyExpensesAmount);

  const { data } = useGraphQLQuery(CoverageSelectionStepOptionsDocument, {
    variables: {
      id: 'emergency_fund:emergency_2',
      monthlyExpensesCents: monthlyExpensesAmount?.cents ?? 0,
      variant: productCategory === 'cash' ? EmergencyFundCoverageVariant.Cash : EmergencyFundCoverageVariant.Investing,
    },
    throwOnError: true,
  });

  const { completeFlow, errors: displayableErrors } = useCompleteDraftPurposeFlow({
    draftEnvelopeId: flowId,
    name: monthlyExpensesDependencies.nameAndImage.name,
    purposeSubType: monthlyExpensesDependencies.nameAndImage.purpose_sub_type,
    imageType: monthlyExpensesDependencies.nameAndImage.imageType,
    productCategory,
  });

  if (!data || data.purposeCreationOption.__typename !== 'EmergencyFundPurposeCreationOption') return null;

  const {
    emergencyFundCoverage: { buffer, options, messages, description },
  } = data.purposeCreationOption;

  const handleSubmit = async (formValues: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    commit({ selectedCoverage: formValues.selectedCoverage });

    if (formValues.selectedCoverage === 'custom') {
      void navigate(`/add_purpose/emergency_fund/${flowId}/custom_coverage`);
      return;
    }

    const opt = options.find(option => String(option.months) === formValues.selectedCoverage);

    await completeFlow({
      targetAmount: opt?.amount,
      purposeInput: {
        emergencyFund: {
          bufferPercent: buffer,
          monthlyExpensesAmount,
        },
      },
    });

    if (displayableErrors.length > 0) {
      formikHelpers.setErrors(convertDisplayableErrors(displayableErrors));
    }
  };

  const handleSkip = async () => {
    await completeFlow({
      purposeInput: {
        emergencyFund: {
          bufferPercent: buffer,
          monthlyExpensesAmount,
        },
      },
    });
  };

  return (
    <WorkflowLayout pageTitle="Emergency Fund Coverage">
      <WorkflowLayout.Header workflowTitle={null} onClose={() => navigate('/')} onBack={() => navigate(-1)} />
      <WorkflowLayout.Content>
        <VStack css={{ gap: 'space.300' }}>
          <VStack css={{ gap: 'space.200' }}>
            <H1 css={{ textWrap: 'auto' }}>How much do you want to save?</H1>
            <Text>{description}</Text>
          </VStack>

          <Formik
            onSubmit={handleSubmit}
            initialValues={{ selectedCoverage: '3' }}
            validationSchema={coverage}
            validateOnChange={false}
          >
            <Form>
              <VStack css={{ gap: 'space.400' }}>
                <FormikField name="selectedCoverage">
                  <FormikField.Control>
                    <RadioGroup>
                      {options.map(option => (
                        <React.Fragment key={option.id}>
                          <Separator />
                          <RadioGroup.ItemContainer>
                            <RadioGroup.Item value={`${option.months}`} />
                            <RadioGroup.ItemLabel css={{ flex: 1 }}>
                              <HStack
                                css={{
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  width: '100%',
                                  py: 'space.200',
                                }}
                              >
                                <VStack css={{ gap: 'space.0', ml: 'space.100' }}>
                                  <Text as="span">
                                    {option.months} months expenses{buffer > 0 ? ` + ${buffer}%` : ''}
                                  </Text>
                                  {buffer > 0 && (
                                    <Text as="span" variant="text50" color="text.weak">
                                      {option.bufferedMonths} months expenses
                                    </Text>
                                  )}
                                </VStack>
                                <Text as="span">{formatMoney(option.amount, 'defaultDollar')}</Text>
                              </HStack>
                            </RadioGroup.ItemLabel>
                          </RadioGroup.ItemContainer>
                        </React.Fragment>
                      ))}
                      <Separator />
                      <RadioGroup.ItemContainer>
                        <RadioGroup.Item value="custom" />
                        <RadioGroup.ItemLabel css={{ flex: 1 }}>
                          <HStack
                            css={{
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              width: '100%',
                              py: 'space.200',
                            }}
                          >
                            <VStack css={{ gap: 'space.0', ml: 'space.100' }}>
                              <Text as="span">Custom amount</Text>
                            </VStack>
                          </HStack>
                        </RadioGroup.ItemLabel>
                      </RadioGroup.ItemContainer>
                      <Separator />
                    </RadioGroup>
                  </FormikField.Control>
                </FormikField>

                <GuidanceBox css={{ maxWidth: '100%' }}>
                  <GuidanceBox.Icon />
                  <GuidanceBox.Header>
                    <H3 variant="title100">{messages[0].title}</H3>
                  </GuidanceBox.Header>
                  <GuidanceBox.Content css={{ display: 'grid', gap: 'space.100' }}>
                    <Text dangerouslySetInnerHTML={{ __html: messages[0].body }} />
                  </GuidanceBox.Content>
                </GuidanceBox>
                <HStack css={{ alignItems: 'center', gap: 'space.400' }}>
                  <Button type="submit" css={{ width: 'fit-content' }}>
                    Continue
                  </Button>
                  <Button variant="textPrimary" onClick={handleSkip}>
                    Skip for now
                  </Button>
                </HStack>
              </VStack>
            </Form>
          </Formik>
        </VStack>
      </WorkflowLayout.Content>
      <WorkflowLayout.Footer>
        <Footer />
      </WorkflowLayout.Footer>
    </WorkflowLayout>
  );
};
